import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getStaff, createStaff, deleteStaff, resetStaffPin, toggleStaffActive } from "@/api/endpoints";
import type { StaffMember } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, RefreshCw, Copy, Check, UserCircle } from "lucide-react";

export function StaffPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const { t } = useI18n();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState<"waiter" | "cashier">("waiter");
  const [copiedPin, setCopiedPin] = useState<string | null>(null);

  const { data: staff = [], isLoading } = useQuery({
    queryKey: ["staff", venueId],
    queryFn: () => getStaff(venueId!),
    enabled: !!venueId,
  });

  const createMut = useMutation({
    mutationFn: () => createStaff(venueId!, { name: newName.trim(), role: newRole }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff", venueId] });
      setAddOpen(false);
      setNewName("");
      setNewRole("waiter");
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteStaff(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", venueId] }),
  });

  const resetPinMut = useMutation({
    mutationFn: (id: string) => resetStaffPin(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", venueId] }),
  });

  const toggleMut = useMutation({
    mutationFn: (id: string) => toggleStaffActive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", venueId] }),
  });

  const copyPin = (pin: string, id: string) => {
    navigator.clipboard.writeText(pin);
    setCopiedPin(id);
    setTimeout(() => setCopiedPin(null), 2000);
  };

  const handleDelete = (s: StaffMember) => {
    if (confirm(t("staff_delete_confirm").replace("{{name}}", s.name))) {
      deleteMut.mutate(s._id);
    }
  };

  const handleResetPin = (s: StaffMember) => {
    if (confirm(t("staff_reset_pin_confirm").replace("{{name}}", s.name))) {
      resetPinMut.mutate(s._id);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("staff")}</h1>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="size-4 mr-1" />
          {t("new_staff")}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-center text-muted-foreground py-8">{t("loading")}</p>
      ) : staff.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">{t("no_staff")}</p>
      ) : (
        <div className="space-y-3">
          {staff.map((s) => (
            <Card key={s._id}>
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <UserCircle className="size-5 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{s.name}</p>
                      <Badge variant="outline" className="text-[10px]">
                        {t(s.role === "waiter" ? "staff_role_waiter" : "staff_role_cashier")}
                      </Badge>
                      <Badge
                        variant={s.isActive ? "default" : "secondary"}
                        className="text-[10px] cursor-pointer"
                        onClick={() => toggleMut.mutate(s._id)}
                      >
                        {s.isActive ? t("staff_active") : t("staff_inactive")}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="font-mono flex items-center gap-1">
                        {t("staff_pin")}: {s.pin}
                        <button onClick={() => copyPin(s.pin, s._id)} className="hover:text-foreground">
                          {copiedPin === s._id ? <Check className="size-3 text-green-500" /> : <Copy className="size-3" />}
                        </button>
                      </span>
                      <span>
                        {t("staff_last_login")}:{" "}
                        {s.lastLogin
                          ? new Date(s.lastLogin).toLocaleDateString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
                          : t("staff_never_logged_in")}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => handleResetPin(s)} title={t("staff_reset_pin")}>
                      <RefreshCw className="size-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(s)} className="text-destructive hover:text-destructive">
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add staff dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("new_staff")}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (newName.trim()) createMut.mutate();
            }}
            className="space-y-4"
          >
            <Input
              placeholder={t("staff_name_placeholder")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2">
              <Badge
                variant={newRole === "waiter" ? "default" : "outline"}
                className="cursor-pointer flex-1 justify-center py-2"
                onClick={() => setNewRole("waiter")}
              >
                {t("staff_role_waiter")}
              </Badge>
              <Badge
                variant={newRole === "cashier" ? "default" : "outline"}
                className="cursor-pointer flex-1 justify-center py-2"
                onClick={() => setNewRole("cashier")}
              >
                {t("staff_role_cashier")}
              </Badge>
            </div>
            <Button type="submit" className="w-full" disabled={!newName.trim() || createMut.isPending}>
              {createMut.isPending ? t("loading") : t("add")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
